import { MultiFormAccordionService } from '@/components/multi-form-accordion/multi-form-accordion.service';
import { ElfBaseStore } from '@/store/base-store.repository';
import { DynamicFieldRepositoryFactory } from '@/store/dynamic-form-state/dynamic-field-state/dynamic-field.repository';
import { DynamicFieldProps } from '@/store/dynamic-form-state/dynamic-field-state/dynamic-field.store';
import { DynamicFormRepository } from '@/store/dynamic-form-state/dynamic-form.repository';
import { DynamicField, DynamicFieldDto } from '@/types';
import { form } from '@/utils/form.utils';
import { dynamicFieldSchema } from '@/validations/dynamic-form.validation';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { select } from '@ngneat/elf';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DynamicFieldsService implements OnDestroy {
  multiFormAccordionService = inject(MultiFormAccordionService);
  dynamicFormRepository = inject(DynamicFormRepository);
  messageService = inject(MessageService);

  private storeSubscriptions: Subscription[] = [];

  initialize() {
    this.subscribeToDynamicFieldStores();
    this.storeSubscriptions.push(
      this.multiFormAccordionService.newStore$.subscribe(store => {
        this.subscribeToStore(store);
      })
    );
    this.subscribeToStores();
  }

  subscribeToDynamicFieldStores() {
    const stores = this.multiFormAccordionService.getAllStores();
    this.storeSubscriptions = stores.map(store => {
      return store.pipe(select(state => state)).subscribe();
    });
  }

  subscribeToStore(store?: ElfBaseStore) {
    const sub = store?.pipe(select(state => state)).subscribe();
    this.storeSubscriptions.push(sub!);
  }

  subscribeToStores() {
    this.subscribeToDynamicFieldStores();
    this.storeSubscriptions.push(
      this.multiFormAccordionService.newStore$.subscribe(store => {
        this.subscribeToStore(store);
      })
    );
    this.storeSubscriptions.push(this.multiFormAccordionService.storeRemoved$.subscribe());
  }

  getAllDynamicFieldsDtos() {
    return this.multiFormAccordionService.getAllStores().map((store: ElfBaseStore<DynamicFieldProps>) => {
      const repo = DynamicFieldRepositoryFactory.create(store);
      return repo.get<DynamicFieldDto>('dto');
    });
  }

  validateAllFieldsDtos(): boolean {
    let isValid = true;

    this.multiFormAccordionService.getAllStores().forEach((store: ElfBaseStore<DynamicFieldProps>, index) => {
      const repo = DynamicFieldRepositoryFactory.create(store);
      const dto = repo.get<DynamicFieldDto>('dto');

      const result = dynamicFieldSchema.safeParse(dto);
      const invalidFormula = dto.isFormula && !dto.formula;
      if (!result.success || invalidFormula) {
        isValid = false;
        const fieldErrors = result.success ? {} : result.error.flatten().fieldErrors;
        if (invalidFormula) {
          fieldErrors.formula = ['La formule est obligatoire.'];
        }
        repo.set('errors', fieldErrors);
        MultiFormAccordionService.openPanelIfClosed(`mfa-${index}`);
        form.scrollToInvalidElement();
      } else {
        repo.set('errors', {});
      }
    });

    return isValid;
  }

  setDynamicFieldDto(response: DynamicField): void {
    const newStore = this.multiFormAccordionService._formFactory?.(response?.dynaFormFieldsId.toString());
    if (!newStore) {
      console.error(`Could not create store for dynamic field with id: ${response.dynaFormFieldsId}`);
      return;
    }

    const root = this.multiFormAccordionService.root;
    if ('children' in root) {
      root.children.push({ store: newStore });
    }

    this.multiFormAccordionService.newStore$.next(newStore);

    const repo = DynamicFieldRepositoryFactory.create(newStore);
    repo.setDynamicFieldDto(response);
  }

  setDynamicFieldDtos(responses: DynamicField[]): void {
    responses.forEach(r => {
      this.setDynamicFieldDto(r);
    });
  }

  restFormType() {
    this.multiFormAccordionService.getAllStores().forEach(store => {
      const repo = DynamicFieldRepositoryFactory.create(store);
      repo.setNested('dto', 'hasTotal', false, false);
      repo.setNested('dto', 'isFormula', false, false);
      repo.setNested('dto', 'formula', '');
    });
  }

  clearAttribute(attribute: string) {
    this.multiFormAccordionService.getAllStores().forEach(store => {
      const repo = DynamicFieldRepositoryFactory.create(store);
      repo.setNested(attribute, undefined);
    });
  }

  clearNestedDtoAttribute(attribute: string) {
    this.multiFormAccordionService.getAllStores().forEach(store => {
      const repo = DynamicFieldRepositoryFactory.create(store);
      repo.setNested('dto', attribute, undefined);
    });
  }

  resetAllDynamicFields() {
    this.multiFormAccordionService.getAllStores().forEach(store => {
      const repo = DynamicFieldRepositoryFactory.create(store);
      repo.reset();
    });
  }

  ngOnDestroy() {
    this.storeSubscriptions.forEach(sub => sub.unsubscribe());
  }
}
